// ---------------------------------------------------------------------------
// What "Apply" does to sender accounts on an ALREADY-LAUNCHED campaign
//
// A launched campaign's Sales Rep split and its existing accounts are fixed
// (its Instantly sub-campaigns are per rep, and started leads still send from
// the account that started them). The ONE change it accepts is ADDING a sender
// account, which the server applies to every not-yet-contacted lead.
//
// The decision is made off the ACCOUNT SETS ONLY — never off the rep-percentage
// assignments. The Sales Rep picker re-normalises those percentages on its own
// (a default-rep effect, an even-split pass), so an assignments-based check
// reported a "rep change" the operator never made and blocked a perfectly valid
// account addition. Keying off the accounts themselves is what makes the common
// case — tick a mailbox, Apply — actually work.
// ---------------------------------------------------------------------------

/** Per-rep account buckets: aiEmployeeId -> account identifiers (email or LinkedIn id). */
export type AccountBuckets = Record<string, string[]>;

/** A Sales Rep assignment as the wizard tracks it — only the id matters here. */
export type RepAssignment = { aiEmployeeId: string; percentage?: number };

/**
 * True when the SET of Sales Rep ids differs between `applied` and `next` — a
 * rep was added, removed, or swapped.
 *
 * Deliberately ignores the percentages. The Sales Rep picker re-normalises them
 * on its own (a default-rep effect, an even-split pass), so a percentage-only
 * diff is not a reliable signal of user intent — reading it as a change is what
 * used to block a valid account addition (UAT 21 Aug). On a launched campaign
 * this is the signal that routes an Apply to the full re-split endpoint rather
 * than the lighter add-a-sender-account one.
 */
export function hasRepSetChange(applied: readonly RepAssignment[], next: readonly RepAssignment[]): boolean {
  const a = new Set(applied.map(x => x.aiEmployeeId));
  const b = new Set(next.map(x => x.aiEmployeeId));
  if (a.size !== b.size) return true;
  for (const id of b) if (!a.has(id)) return true;
  return false;
}

/** True when `next` contains at least one account that `prev` did not, for any rep. */
export function hasAccountAdditions(prev: AccountBuckets, next: AccountBuckets): boolean {
  for (const aie of Object.keys(next)) {
    const had = new Set(prev[aie] ?? []);
    if ((next[aie] ?? []).some(a => !had.has(a))) return true;
  }
  return false;
}

/** True when `prev` contains at least one account that `next` no longer has, for any rep. */
export function hasAccountRemovals(prev: AccountBuckets, next: AccountBuckets): boolean {
  // A removal from prev→next is exactly an addition from next→prev.
  return hasAccountAdditions(next, prev);
}

export type SenderAccountAction = 'apply' | 'blocked-removal' | 'nothing-added';

/**
 * Decide what applying the Sender Account step should do on a launched campaign.
 *
 *   - `blocked-removal`: an existing account was unticked. Removing a sender from
 *     a launched campaign would strand the leads already sending from it, so it
 *     is refused — removals take precedence over any addition in the same edit.
 *   - `apply`: at least one new account was added (and nothing removed). Send the
 *     enlarged account maps to the server, whatever happened to the percentages.
 *   - `nothing-added`: no account changed. Any percentage-split edit alone cannot
 *     be applied to a launched campaign, so there is nothing to do.
 */
export function senderAccountApplyAction(
  appliedEmail: AccountBuckets,
  nextEmail: AccountBuckets,
  appliedLinkedin: AccountBuckets,
  nextLinkedin: AccountBuckets,
): SenderAccountAction {
  if (hasAccountRemovals(appliedEmail, nextEmail) || hasAccountRemovals(appliedLinkedin, nextLinkedin)) {
    return 'blocked-removal';
  }
  if (hasAccountAdditions(appliedEmail, nextEmail) || hasAccountAdditions(appliedLinkedin, nextLinkedin)) {
    return 'apply';
  }
  return 'nothing-added';
}

/** Order-insensitive equality of Sales Rep assignments on (aiEmployeeId, percentage). */
function sameAssignments(a: readonly RepAssignment[], b: readonly RepAssignment[]): boolean {
  if (a.length !== b.length) return false;
  const pctById = new Map(b.map(x => [x.aiEmployeeId, x.percentage]));
  return a.every(x => pctById.get(x.aiEmployeeId) === x.percentage);
}

/**
 * Order-insensitive equality of per-Sales-Rep account buckets. Empty and
 * missing buckets are the same thing — a rep with `[]` and a rep with no entry
 * at all both mean "no accounts selected", and the two shapes alternate as reps
 * are toggled on and off.
 */
function sameBuckets(a: AccountBuckets, b: AccountBuckets): boolean {
  const ids = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const id of ids) {
    const left = [...(a[id] ?? [])].sort();
    const right = [...(b[id] ?? [])].sort();
    if (left.length !== right.length || left.some((v, i) => v !== right[i])) return false;
  }
  return true;
}

export interface SalesRepAutoApplyInput {
  /** Editing a campaign that already left 'draft' — the only mode where the rep block does NOT ride the plain autosave and so needs its own commit. */
  isEditingNonDraft: boolean;
  /** The campaign has been launched (leads are already sending). */
  launched: boolean;
  /**
   * `salesRepApplyGate() === null` in the wizard: the rep block is valid and
   * complete — at least one rep, shares total 100%, and every rep that sends on
   * a required channel has an account (or a fallback). Computed by the caller
   * because it depends on the campaign's required channels + fallbacks.
   */
  valid: boolean;
  appliedAssignments: readonly RepAssignment[];
  nextAssignments: readonly RepAssignment[];
  appliedEmail: AccountBuckets;
  nextEmail: AccountBuckets;
  appliedLinkedin: AccountBuckets;
  nextLinkedin: AccountBuckets;
}

/**
 * Whether a Sales-Rep / sender-account edit should COMMIT ITSELF now.
 *
 * Editing an already-prepared campaign has no "Apply" button (removed so edit
 * feels like create — LinkedIn-UAT 4 Sep): a valid change commits on a debounce
 * like every other field. This is the gate that decides when. It says yes ONLY
 * when the change is:
 *   - in edit mode (create/draft autosaves the whole rep block itself),
 *   - VALID/complete (an incomplete picker keeps its quiet inline hint and
 *     nothing is committed — the same way create won't advance an invalid step),
 *   - actually DIRTY versus the applied server state, and
 *   - APPLICABLE: on a launched campaign with no rep-set change, only an account
 *     ADDITION can be applied — a removal is blocked (it would strand sending
 *     leads) and a bare percentage tweak is a no-op, so neither should auto-fire
 *     (nor toast). A rep add/remove/swap always routes to the full re-split.
 */
export function shouldAutoApplySalesReps(inp: SalesRepAutoApplyInput): boolean {
  if (!inp.isEditingNonDraft) return false;
  if (!inp.valid) return false;
  const dirty =
    !sameAssignments(inp.appliedAssignments, inp.nextAssignments) ||
    !sameBuckets(inp.appliedEmail, inp.nextEmail) ||
    !sameBuckets(inp.appliedLinkedin, inp.nextLinkedin);
  if (!dirty) return false;
  if (inp.launched && !hasRepSetChange(inp.appliedAssignments, inp.nextAssignments)) {
    return (
      senderAccountApplyAction(inp.appliedEmail, inp.nextEmail, inp.appliedLinkedin, inp.nextLinkedin) === 'apply'
    );
  }
  return true;
}
